import React, {
  forwardRef,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  getObjectPosition,
  getSrc,
  getSrcSet,
  mobileBreakpointInPixels,
} from 'src/helpers/hosted-asset-helpers';
import type { CSSInterpolation } from '@emotion/serialize';
import Helmet from 'react-helmet';
import { inlineImageDataIdAttribute } from 'src/enums/build/inline-image-data-id';
import { SiteContext } from 'src/context/site-context';
import { activeEnv } from '../../../build-scripts/environment';

export type HostedImageDetails = Queries.ImageDetails & {
  mobileCrop?: Queries.ImageCrop;
  desktopCrop?: Queries.ImageCrop;
};

export type HostedImageProps = {
  imageDetails: HostedImageDetails | undefined | null;
  styles?: CSSInterpolation;
  loading?: 'eager' | 'lazy';
  onImageLoaded?: () => void;
  enablePreload?: boolean;
  imageWidthLimit?: number;
  enableInlineDataSrc?: boolean;
};

const mobileCropMedia = `(max-width: ${mobileBreakpointInPixels}px)`;

const FALLBACK_INLINE_IMAGE_WIDTH = 100;

/**
 * We substitute the image's remote URL for inline (base64) data using the following process:
 *  1. During static page generation, place a {@link inlineImageDataIdAttribute} on the img and source nodes.
 *  2. Afterward (see {@link onPostBuild}), download the remote images marked with that attribute, convert to base64,
 *     and put that data in both the HTML document and its corresponding page-data.json?v=2# file.
 * When the user loads their first page, the HTML document will already contain the inline image.
 * During/after hydration (including SPA navigation), the inline image data will be found in the {@link SiteContext}.
 * Therefore, after hydration, this function can *expect* to find inline image data in the context (unless the page is
 * hot reloading); it won't find any inline image data during static page generation.
 */
const substituteInlineImageDataAfterHydration = (
  url: string,
  inlineImageData?: Record<string, string>
) => {
  if (!url) {
    return url;
  }

  const foundInlineImageData = inlineImageData?.[url];
  if (!foundInlineImageData) {
    if (typeof document !== 'undefined' && activeEnv !== 'development') {
      // Hydrating and definitely not hot reloading
      // eslint-disable-next-line no-console
      console.error(`Unable to find the inline image data for ${url} while hydrating!`);
    }
    return url;
  }

  return foundInlineImageData;
};

const HostedImage = forwardRef<HTMLImageElement, HostedImageProps>(
  (
    {
      imageDetails,
      styles,
      loading,
      onImageLoaded = () => {},
      enablePreload = false,
      imageWidthLimit,
      enableInlineDataSrc,
    },
    ref
  ) => {
    const { inlineImageData } = useContext(SiteContext);
    const innerRef = useRef<HTMLImageElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current!, []);

    const imagePath = imageDetails?.imagePath;

    useEffect(() => {
      if (innerRef.current?.complete) {
        onImageLoaded?.();
      }
    }, []);

    const baseInlineDataSrcId = useId();
    const [inlineDataSrcId, mobileInlineDataSrcId] = enableInlineDataSrc
      ? [baseInlineDataSrcId, `${baseInlineDataSrcId}_mobile`]
      : [undefined, undefined];

    if (!imageDetails || !imagePath) {
      return null;
    }

    const src = enableInlineDataSrc
      ? substituteInlineImageDataAfterHydration(
          getSrc(
            imagePath,
            imageWidthLimit ?? FALLBACK_INLINE_IMAGE_WIDTH,
            imageDetails.desktopCrop
          ),
          inlineImageData
        )
      : getSrc(imagePath, imageWidthLimit, imageDetails.desktopCrop);

    const srcSet = enableInlineDataSrc
      ? undefined
      : getSrcSet(imagePath, imageWidthLimit, imageDetails.desktopCrop);

    const cropForMobile = imageDetails.mobileCrop ?? imageDetails.desktopCrop;

    const mobileCropSrcSet = !!cropForMobile
      ? enableInlineDataSrc
        ? substituteInlineImageDataAfterHydration(
            getSrc(imagePath, imageWidthLimit ?? FALLBACK_INLINE_IMAGE_WIDTH, cropForMobile),
            inlineImageData
          )
        : getSrcSet(imagePath, imageWidthLimit, cropForMobile)
      : undefined;

    return (
      <>
        {enablePreload && !enableInlineDataSrc && (
          <Helmet>
            <link rel="preload" fetchPriority="high" as="image" href={src} imageSrcSet={srcSet} />
            {imageDetails.mobileCrop && (
              <link
                rel="preload"
                fetchPriority="high"
                as="image"
                href={src}
                imageSrcSet={mobileCropSrcSet}
                media={mobileCropMedia}
              />
            )}
          </Helmet>
        )}
        <picture>
          {imageDetails.mobileCrop && (
            <source
              srcSet={mobileCropSrcSet}
              media={mobileCropMedia}
              {...{
                [inlineImageDataIdAttribute]: mobileInlineDataSrcId,
              }}
            />
          )}
          <img
            ref={innerRef}
            srcSet={srcSet} /* srcSet must come before src */
            src={src}
            alt={imageDetails?.alternateText ?? ''}
            css={[
              {
                width: '100%',
                height: '100%',
                objectFit: `cover`,
                objectPosition: getObjectPosition(imageDetails),
              },
              styles,
            ]}
            loading={loading}
            onLoad={onImageLoaded}
            {...{
              [inlineImageDataIdAttribute]: inlineDataSrcId,
            }}
          />
        </picture>
      </>
    );
  }
);

HostedImage.displayName = 'HostedImage';

export default HostedImage;
